import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

export interface TableOfContentsItem {
  id: string;
  name: string;
  displayName: string;
  level: number;
  error?: string;
}

interface TableOfContentContextType {
  items: TableOfContentsItem[];
  activeItemId: string | null;
  addItem: (item: TableOfContentsItem) => void;
  removeItem: (id: string) => void;
  setActiveItem: (id: string) => void;
}

const sortElementsByDOMPosition = (elements: HTMLElement[]) => {
  return elements.sort((a, b) => {
    const position = a.compareDocumentPosition(b);
    return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });
};

const TableOfContentsContext = createContext<
  TableOfContentContextType | undefined
>(undefined);

export const TableOfContentsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [items, setItems] = useState<TableOfContentsItem[]>([]);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const addItem = useCallback((item: TableOfContentsItem) => {
    setItems((prev) => [...prev, item]);
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const setActiveItem = useCallback((id: string) => {
    setActiveItemId(id);
  }, []);

  const sortedItems = useMemo(() => {
    const elements = items
      .map((item) => {
        const element = document.getElementById(item.id);
        return element;
      })
      .filter((element) => element !== null);
    const sortedElements = sortElementsByDOMPosition(elements);
    return sortedElements
      .map((element) => {
        return items.find((item) => item.id === element?.id);
      })
      .filter((item) => item !== undefined) as TableOfContentsItem[];
  }, [items]);

  const value = useMemo(
    () => ({
      items: sortedItems,
      activeItemId,
      addItem,
      removeItem,
      setActiveItem,
    }),
    [sortedItems, activeItemId, addItem, removeItem, setActiveItem],
  );

  return (
    <TableOfContentsContext.Provider value={value}>
      {children}
    </TableOfContentsContext.Provider>
  );
};

export const useTableOfContext = () => {
  const context = useContext(TableOfContentsContext);
  if (!context) {
    throw new Error(
      'useTableOfContext must be used within a TableOfContentProvider',
    );
  }
  return context;
};
